/* @HEADER
 * @version 1.1.158 | 2026-10-17
 * @purpose The seam for resets of state a module pinned to a person's identity, run when the identity changes (a logout, the sign-out wipe, a login as someone else): the wiring from the auth adapter and the window, and a re-export of the registry in identityResetRegistry.ts. Framework-free; the window and the auth adapter arrive as arguments.
 * @sidecar identityScopedState.ts.header.md
 * @layer app | @hex domain | @ctx auth
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7443, ZVX-DEV-7440

import { SIGN_OUT_WIPED_EVENT } from './signOutWipeHost.ts'
import { runIdentityScopedResets } from './identityResetRegistry.ts'

// The registry itself -- the list, `registerIdentityScopedReset`, `runIdentityScopedResets` and their types -- lives in
// identityResetRegistry.ts, which imports nothing, so that a module can register at LOAD without taking the sign-out
// wipe and the database core with it into a node:test run. Re-exported here so that every caller's import path is
// unchanged; this file keeps the wiring and holds no list of its own.
export { registerIdentityScopedReset, runIdentityScopedResets } from './identityResetRegistry.ts'
export type { IdentityChangeReason, IdentityScopedReset } from './identityResetRegistry.ts'

/** The part of an `AuthChangeEvent` the wiring reads. */
export interface IdentityAuthEvent {
  type: 'login' | 'logout'
  user: { id: string } | null
}

export interface IdentityScopedResetWiringDeps {
  adapter: {
    getUser(): { id: string } | null
    onAuthChange(listener: (event: IdentityAuthEvent) => void): void
    offAuthChange(listener: (event: IdentityAuthEvent) => void): void
  }
  win: {
    addEventListener(type: string, listener: () => void): void
    removeEventListener(type: string, listener: () => void): void
  }
}

/**
 * Run the resets when the IDENTITY changes, not on every auth event: a logout with nobody left, the sign-out wipe,
 * and a login as someone other than the person remembered. The remembered person lives in this installation's closure.
 * Returns the teardown.
 */
export function installIdentityScopedResetWiring(deps: IdentityScopedResetWiringDeps): () => void {
  const { adapter, win } = deps
  let lastUserId: string | null = adapter.getUser()?.id ?? null

  const onAuthChange = (event: IdentityAuthEvent): void => {
    if (event.type === 'logout') {
      // A logout that still carries a person is not a change of identity: JwtAuthAdapter.ts:535 (markRefreshFailed)
      // sends one after a failed token refresh -- the tokens are gone, the person is KEPT and getUser() still answers.
      // The discriminator is the user in the event: a real sign-out (:443, :257 with nobody left, the local adapter's
      // logout) is sent after setUser(null), so its user is null. Nothing runs and the person stays remembered, so the
      // same person logging in afterwards runs nothing either, and someone else is caught by the login branch.
      if (event.user !== null) return
      lastUserId = null
      runIdentityScopedResets('logout')
      return
    }
    const nextUserId = event.user?.id ?? null
    const changed = nextUserId !== lastUserId
    lastUserId = nextUserId
    if (changed) runIdentityScopedResets('login')
  }
  const onWiped = (): void => runIdentityScopedResets('wiped')

  adapter.onAuthChange(onAuthChange)
  win.addEventListener(SIGN_OUT_WIPED_EVENT, onWiped)

  return () => {
    adapter.offAuthChange(onAuthChange)
    win.removeEventListener(SIGN_OUT_WIPED_EVENT, onWiped)
  }
}
